import React, { useEffect, useState } from 'react';
// import toast from 'react-hot-toast';

import { Form, Formik } from 'formik';
import * as yup from "yup";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { DotsHorizontalIcon } from "@heroicons/react/outline";

import { useClassNames } from "../../../hooks/useClassNames";
import { useModalHelper } from "@metaforcelabs/metaforce-core";

import { getTextLibraryFolders, validateTextLibraryFolders } from "../../../api/interactTemplateFileProcess.js";

import Modal from '../../../components/Modal';
import { GenericErrorModal } from '../../../components/Modals/genericErrorModal.js';

import { EditSelectedTextLibraryFoldersModal } from "./editSelectedTextLibraryFoldersModal.js";

import { Button as CmpButton } from "../../../components/Button";
import { Label as CmpLabel } from "../../../components/Label";
import { SearchableSelect as CmpSelect } from '../../../components/SearchableSelect';
import { Select } from '../../../components/Select';
import { Checkbox } from "../../../components/Checkbox";
import { TextArea } from '../../../components/Form/TextArea';
import { TwFormEx } from '../../../components/TailwindForm';
import { DistributionListsStatus } from '../../../components/DistributionListStatusBadge';

export const pdfAttachmentPolicyOptions = [
  { name: "Follow global setting", value: 1 },
  { name: "Not allowed", value: 2 },
  { name: "Allowed", value: 3 }
];

export function EditLogicDocumentProcessesModal({ modalHelper, title, onConfirm, isSaving, workflowTriggers }) {
  useClassNames();

  const [isBusy, setIsBusy] = useState(false)
  const [busyMessage, setBusyMessage] = useState("")

  const [wfPreProcess, setWfPreProcess] = useState([])
  const [wfPostPreProcess, setWfPostPreProcess] = useState([])
  const [wfPostProcess, setWfPostProcess] = useState([])
  const [smartformsOptions, setSmartformsOptions] = useState([]);
  const [loadedTextLibraryFolders, setLoadedTextLibraryFolders] = useState([]);
  const [folderNamesData, setFolderNamesData] = useState(null);
  const [distributionListOptions, setDistributionListOptions] = useState([]);

  const [openGenericErrorModal, setOpenGenericErrorModal] = useState(false);
  const [genericErrorModalData, setGenericErrorModalData] = useState({ title: "", contentJsx: <></> });

  const editSelectedTextLibraryFoldersModalHelper = useModalHelper();

  useEffect(() => {
    setIsBusy(isSaving);
    setBusyMessage(isSaving ? `Saving workflows for document ${modalHelper.activeItem?.logicFile.fileName}...` : null);
  }, [isSaving]);

  useEffect(async () => {
    if (modalHelper.activeItem) {
      setBusyMessage("Loading workflows for logic file...");
      setIsBusy(true);

      if (!modalHelper.activeItem?.item.pdfAttachmentPolicy) {
        modalHelper.activeItem.item.pdfAttachmentPolicy = modalHelper.activeItem.pdfAttachmentPolicyOptions[0].value;
      }

      try {
        let folders = await getTextLibraryFolders();
        folders = folders.map(f => { return {...f, fullName: getFolderName(f, folders) } })
        setLoadedTextLibraryFolders(folders);
        const namesData = getFolderNames(modalHelper.activeItem?.item.textLibraryFolders, folders);
        if (namesData.invalidIds.length > 0)
        {
          setGenericErrorModalData({title: "Invalid folder ids found", contentJsx: 
            <>
              {namesData.invalidIds.map(id => (
                <div>{id}</div>
              ))}
            </>
           });
          setOpenGenericErrorModal(true);
        }
        setFolderNamesData(namesData)

        setWfPreProcess(filterWorkflowsAndAddDefault(modalHelper.activeItem?.workflows, workflowTriggers[0]));
        setWfPostPreProcess(filterWorkflowsAndAddDefault(modalHelper.activeItem?.workflows, workflowTriggers[1]));
        setWfPostProcess(filterWorkflowsAndAddDefault(modalHelper.activeItem?.workflows, workflowTriggers[2]));
        setSmartformsOptions(filterSmartformsAndAddDefault(modalHelper.activeItem?.smartforms || []));
        setDistributionListOptions(filterDistributionListsAndAddDefault(modalHelper.activeItem?.distributionLists || []));
      } finally {
        setIsBusy(false);
        setBusyMessage(null);
      }
    }
  }, [modalHelper.activeItem])

  const filterWorkflowsAndAddDefault = (workflows, filter) => {
    const defaultList = [{ id: '', name: 'None' }];
    var list = defaultList.concat(workflows.filter(x => !!x.workflowTags.find(z => z === filter)) || []);
    return list.map(x => { x["value"] = x.id; return x });
  }

  const filterSmartformsAndAddDefault = (smartforms) => {
    const defaultList = [{ id: '', name: 'None' }];
    var list = defaultList.concat(smartforms || []);
    return list.map(x => { x["value"] = x.id; return x });
  }

  const filterDistributionListsAndAddDefault = (distributionLists) => {
    const current = modalHelper.activeItem?.item.distributionListId || '';
    const enabledLists = distributionLists.filter(dl => dl.status === DistributionListsStatus.active);
    const defaultList = [{ id: '', name: 'None' }];
    var list = defaultList.concat(enabledLists || []);
    // if current is disabled, add it to the list
    if (current && !list.find(dl => dl.id === current)) {
      const currentDl = distributionLists.find(dl => dl.id === current);
      if (currentDl && currentDl.status === DistributionListsStatus.disabled) {
        list.push({ id: currentDl.id, name: currentDl.name + ' (disabled)', disabled: true });
      }
    }
    return list.map(x => { x["value"] = x.id; return x });
  }

  const clearData = (item) => {
    var item = modalHelper.activeItem?.item;
    if (!item)
      return;

    item.workflowEnvironment = 0;
    item.postProcessWorkflowId = "";
    item.prePostProcessWorkflowId = "";
    item.preProcessWorkflowId = "";
    item.smartformId = "";
    item.distributionListId = "";
    item.forceToRunSmartformBeforeEditorSession = false
    item.isAttachment = false;
    item.pdfAttachmentPolicy = pdfAttachmentPolicyOptions[0].value;
  }

  const onEditTextlibraryFolders = (props, value) => {
    editSelectedTextLibraryFoldersModalHelper.setActivetItem({
      loadedTextLibraryFolders: loadedTextLibraryFolders,
      props: props,
      item: getFolderIds(value?.toString() || "").ids,
    });
    editSelectedTextLibraryFoldersModalHelper.open();
  }

  const updateSelectedFolders = async (props, selectedFolderIds) => {
    // Validate folders before saving
    const result = await validateSelectedFolders(selectedFolderIds);
    if (!result.isValid) {
      setGenericErrorModalData({title: "Invalid folder ids found", contentJsx: 
        <>
          {result.invalidData.map(data => (
            <div>{data.id}</div>
          ))}
        </>
      });
      setOpenGenericErrorModal(true);
    } else {
      // Set folders
      props.setFieldValue("textLibraryFolderNames", getFolderNames(selectedFolderIds).names);
      editSelectedTextLibraryFoldersModalHelper.close();
    }
  }

  const validateSelectedFolders = async (selectedFolders) => {
    const ids = selectedFolders.split(";").filter(id => !!id);
    const result = ids.length > 0 
      ? await validateTextLibraryFolders({ folderIds: selectedFolders.split(";") }) 
      : [];
    var invalidData = result.length > 0 ? result.filter(r => !r.isValid) : [];
    return { isValid: invalidData.length === 0, invalidData: invalidData };
  }

  const getFolderIds = (folderNames) => {
    let ids = "";
    let invalidNames = [];
    const names = folderNames?.split(";") || [];
    names.map(fn => {
        const id = getFolderId(fn);
        if (!id && !!fn)
          invalidNames.push(fn);
        return id;
      }).filter(id => !!id).forEach(id => {
      if (ids.length > 0)
        ids += ";";
      ids += id;
    });
    return {ids: ids, invalidNames: invalidNames };
  }

  const getFolderId = (folderName) => {
    const folder = loadedTextLibraryFolders.find(f => f.fullName === folderName);
    return folder?.id;
  }

  const getFolderName = (folder, folders) => {
      let fullName = "";
      if (folder.name.length > 0)
      {
        fullName += '\\';
        if (!!folder.parentFolderId) {
          var parent = folders.find(a => a.id === folder.parentFolderId);
          fullName += parent.name;
          fullName += '\\';
        }
        fullName += folder.name;
      }
      return fullName;
  }

  const getFolderNames = (folderIds, folders) => {
      let selectedNames = "";
      let invalidIds = [];

      folders = folders || loadedTextLibraryFolders;
      const selectedItems = folderIds?.split(";") || [];
      selectedItems.map(id => {
        var f = folders.find(a => a.id === id);
        if (!f && !!id)
          invalidIds.push(id);
        return f;
      }).filter(f => !!f)
        .forEach(f => {
          if (f.name.length > 0)
          {
            if (selectedNames.length > 0)
              selectedNames += ";";
            selectedNames += getFolderName(f, folders);
          }
        }
      );
      return { names: selectedNames, invalidIds: invalidIds };
  }

  const handleFoldersNameChange = (value, props) => {
    props.setFieldValue("textLibraryFolderNames", value.replace(/(\r\n|\n|\r)/gm, ""));
  }

  const handleSubmit = async (values) => {
    // Validate text library folders
    var res = getFolderIds(values.textLibraryFolderNames)
    if (res.invalidNames.length > 0)
    {
      setGenericErrorModalData({title: "Invalid folder names found", contentJsx: 
        <>
         {res.invalidNames.map(name => (
           <div>{name}</div>
         ))}
        </>
      });
      setOpenGenericErrorModal(true);
    } else {
      delete values.textLibraryFolderNames;
      values = {...values, textLibraryFolders: res.ids};
      await onConfirm(modalHelper.activeItem?.item, values);
    }
  }

  return (
    <Modal
      isOpen={modalHelper.isOpen}
      onClose={() => { modalHelper.close() }}
      size={'large'}
      title={title}
    >
      {isBusy && (
        <>
          <div className="flex justify-center">
            <FontAwesomeIcon icon={faSpinner} className="text-white" size="4x" spin bounce />
          </div>
          <div className="flex justify-center mt-4">
            <CmpLabel text={busyMessage} />
          </div>
        </>
      )}

      {!isBusy && modalHelper.activeItem && (
        <>
          <CmpLabel text="Attaching workflows to:" textClass='text-sm' />
          <CmpLabel text={`${modalHelper.activeItem?.environment.environment.name} / ${modalHelper.activeItem?.logicFile.fileName} / ${modalHelper.activeItem?.item.documentName}`} textClass='text-lg font-bold' />

          <div className="space-y-10 divide-y divide-gray-900/10 ">
            <Formik
              // enableReinitialize={true}
              initialValues={{
                fileId: modalHelper.activeItem?.logicFile.id,
                templateFileName: modalHelper.activeItem?.logicFile.fileName,
                templateId: modalHelper.activeItem?.environment.template.id,
                documentId: modalHelper.activeItem?.item.documentId,
                documentName: modalHelper.activeItem?.item.documentName,
                documentDescription: modalHelper.activeItem?.item.label,
                environmentGroupId: modalHelper.activeItem?.environmentGroup.id,
                customerEnvironment: modalHelper.activeItem?.environment.template.customerEnvironment,
                workflowEnvironment: parseInt(modalHelper.activeItem?.item.workflowEnvironment || modalHelper.activeItem?.environments[0].value || 1),
                preProcessWorkflowId: modalHelper.activeItem?.item.preProcessWorkflowId,
                prePostProcessWorkflowId: modalHelper.activeItem?.item.prePostProcessWorkflowId,
                postProcessWorkflowId: modalHelper.activeItem?.item.postProcessWorkflowId,
                smartformId: modalHelper.activeItem?.item.smartformId,
                textLibraryFolderNames: folderNamesData?.names,
                distributionListId: modalHelper.activeItem?.item.distributionListId,
                forceToRunSmartformBeforeEditorSession: modalHelper.activeItem?.item.forceToRunSmartformBeforeEditorSession || false,
                isAttachment: modalHelper.activeItem?.item.isAttachment || false,
                pdfAttachmentPolicy: modalHelper.activeItem?.item.pdfAttachmentPolicy || [],
              }}
              validationSchema={yup.object().shape({
                preProcessWorkflowId: yup.string(),
                prePostProcessWorkflowId: yup.string(),
                postProcessWorkflowId: yup.string(),
                smartformId: yup.string(),
                textLibraryFolderNames: yup.string(),
                forceToRunSmartformBeforeEditorSession: yup.boolean(),
                isAttachment: yup.boolean()
              })
              }
              onSubmit={handleSubmit}
            >
              {({ values, errors, ...props }) => (
                <Form>
                  <div className="space-y-4">
                    <TwFormEx.Section />
                    <TwFormEx.Section label="Smartform Preloader" description="Will be connected to the template in order to Load editor with custom data" separator={false}>
                      <CmpSelect
                        className={"px-0"}
                        label="Smartform preloader"
                        name="smartformId"
                        value={values?.smartformId}
                        onChange={props.handleChange}
                        options={smartformsOptions}
                        error={errors?.smartformId}
                      />
                      <div className="mt-4 mb-auto">
                        <Checkbox
                          id="forceToRunSmartformBeforeEditorSession"
                          name="forceToRunSmartformBeforeEditorSession"
                          label="Force to run before editor session"
                          value={values?.forceToRunSmartformBeforeEditorSession}
                          onChange={props.handleChange}
                          error={errors?.forceToRunSmartformBeforeEditorSession}
                        />
                      </div>
                    </TwFormEx.Section>
                    <TwFormEx.Section label="Workflows" description="Add workflows for the specific editor events" separator={false}>
                      <CmpSelect
                        className={"px-0"}
                        label="Editor On Load"
                        name="preProcessWorkflowId"
                        value={values?.preProcessWorkflowId}
                        onChange={props.handleChange}
                        options={wfPreProcess}
                        error={errors?.preProcessWorkflowId}
                      />
                      <CmpSelect
                        className={"px-0"}
                        label="QA Before Completion"
                        name="prePostProcessWorkflowId"
                        value={values?.prePostProcessWorkflowId}
                        onChange={props.handleChange}
                        options={wfPostPreProcess}
                        error={errors?.prePostProcessWorkflowId}
                      />
                      <CmpSelect
                        className={"px-0"}
                        label="Editor On Completed"
                        name="postProcessWorkflowId"
                        value={values?.postProcessWorkflowId}
                        onChange={props.handleChange}
                        options={wfPostProcess}
                        error={errors?.postProcessWorkflowId}
                      />
                    </TwFormEx.Section>
                    <TwFormEx.Section label="Distribution" description="Attach a distribution list" separator={false}>
                      <CmpSelect
                        className={"px-0"}
                        label="Distribution List"
                        name="distributionListId"
                        value={values?.distributionListId}
                        onChange={props.handleChange}
                        options={distributionListOptions}
                        error={errors?.distributionListId}
                      />
                    </TwFormEx.Section>
                    <TwFormEx.Section label="Textlibrary folders" description="Add Textlibrary folders to make available folders such as Property, Cargo, etc." separator={false}>
                      <div className="flex items-start">
                        <TextArea
                            name="Textlibrary folders" 
                            value={values?.textLibraryFolderNames}
                            onChange={v => handleFoldersNameChange(v, props)}
                            error={errors?.textLibraryFolderNames}
                        />
                        <CmpButton
                          type="button"
                          variant={CmpButton.variants.secondary}
                          size={CmpButton.sizes.md}
                          className="ml-2"
                          onClick={() => onEditTextlibraryFolders(props,values?.textLibraryFolderNames)}
                        >
                          <DotsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                        </CmpButton>
                    </div>                    
                    </TwFormEx.Section>
                    <TwFormEx.Section label="Document Tags" separator={false}>
                      <Checkbox
                        id="isAttachment"
                        name="isAttachment"
                        label="Document is an attachment to be used in Editor"
                        value={values?.isAttachment}
                        onChange={props.handleChange}
                        error={errors?.isAttachment}
                      />
                    </TwFormEx.Section>
                    <TwFormEx.Section label="PDF attachments" separator={false}>
                      <Select
                        label="Allow PDF Attachments from Disk"
                        name="pdfAttachmentPolicy"
                        value={values?.pdfAttachmentPolicy}
                        onChange={props.handleChange}
                        options={pdfAttachmentPolicyOptions}
                        error={errors?.pdfAttachmentPolicy}
                      />
                    </TwFormEx.Section>
                  </div>

                  <div className='grid grid-cols-2 mt-8'>
                    <div className='flex gap-x-4'>
                      {/* <CmpButton
                        variant={CmpButton.variants.secondary}
                        className="justify-center"
                        disabled={false}
                        onClick={() => {
                        }}
                      >
                        Clear
                      </CmpButton> */}
                    </div>
                    <div className='flex gap-x-4 justify-end'>
                      <CmpButton
                        variant={CmpButton.variants.secondary}
                        className="justify-center"
                        disabled={false}
                        onClick={() => {
                          modalHelper.close();
                        }}
                      >
                        Cancel
                      </CmpButton>
                      <CmpButton
                        variant={CmpButton.variants.primary}
                        className="justify-center"
                        disabled={false}
                        type={"submit"}
                      >
                        Save
                      </CmpButton>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <EditSelectedTextLibraryFoldersModal
            title={`Edit Selected Textlibrary Folders`}
            modalHelper={editSelectedTextLibraryFoldersModalHelper}
            onConfirm={updateSelectedFolders}
            isSaving={isSaving}
          />

          <GenericErrorModal
            open={openGenericErrorModal}
            setOpen={setOpenGenericErrorModal}
            titel={genericErrorModalData.title}
            contentJsx={genericErrorModalData.contentJsx}
          />
        </>
      )}
    </Modal>
  )
}

